import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { SafeHtmlPipe } from '../../pipes/safe-html-pipe';

type HistoryDetailsBlock = {
  type: 'text' | 'html';
  content?: string;
  label?: string;
  html?: string;
};

const HTML_LABELS = new Set([
  'Treść',
  'Treść (przed)',
  'Treść (po)',
  'Treść regulaminu',
  'Treść regulaminu (przed)',
  'Treść regulaminu (po)',
]);

const SECTION_LABELS = new Set([
  'Dane wejściowe',
  'Dane wyjściowe',
  'Dane przed zmianą',
  'Dane po zmianie',
]);

@Component({
  selector: 'app-history-event-details',
  standalone: true,
  imports: [CommonModule, SafeHtmlPipe],
  template: `
    <div class="flex flex-col gap-3">
      <ng-container *ngFor="let block of blocks(); trackBy: trackByIndex">
        <div *ngIf="block.type === 'text'; else htmlBlock" class="whitespace-pre-line">
          {{ block.content }}
        </div>

        <ng-template #htmlBlock>
          <div>
            <div class="font-medium whitespace-pre-line">{{ block.label }}</div>
            <div [innerHTML]="(block.html || '') | safeHtml"></div>
          </div>
        </ng-template>
      </ng-container>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HistoryEventDetailsComponent {
  details = input<string | null | undefined>();

  protected readonly blocks = computed(() => this.parseDetails(this.details()));

  protected trackByIndex(index: number): number {
    return index;
  }

  private parseDetails(details: string | null | undefined): HistoryDetailsBlock[] {
    if (!details || !details.trim()) {
      return [{ type: 'text', content: '-' }];
    }

    const lines = this.normalizeSectionLines(details.split(/\r?\n/));
    const blocks: HistoryDetailsBlock[] = [];
    const textBuffer: string[] = [];

    let index = 0;
    while (index < lines.length) {
      const line = lines[index];
      const htmlLabel = this.extractHtmlLabel(line);

      if (!htmlLabel) {
        textBuffer.push(line);
        index++;
        continue;
      }

      this.flushTextBlock(textBuffer, blocks);
      index++;

      const htmlLines: string[] = [];
      while (index < lines.length && !this.isDetailsBlockStart(lines[index])) {
        htmlLines.push(lines[index]);
        index++;
      }

      blocks.push({
        type: 'html',
        label: htmlLabel,
        html: htmlLines.join('\n').trim() || '-',
      });
    }

    this.flushTextBlock(textBuffer, blocks);
    return blocks;
  }

  private flushTextBlock(textBuffer: string[], blocks: HistoryDetailsBlock[]): void {
    if (!textBuffer.length) {
      return;
    }

    const content = textBuffer.join('\n').trim();
    if (content) {
      blocks.push({ type: 'text', content });
    }

    textBuffer.length = 0;
  }

  private extractHtmlLabel(line: string): string | null {
    if (!this.isBulletLine(line)) {
      return null;
    }

    const label = this.extractFieldLabel(line);
    return label && HTML_LABELS.has(label) ? label : null;
  }

  private isBulletLine(line: string): boolean {
    return this.extractFieldLabel(line) !== null;
  }

  private isDetailsBlockStart(line: string): boolean {
    return this.isBulletLine(line) || this.extractSection(line) !== null;
  }

  private normalizeSectionLines(lines: string[]): string[] {
    const normalizedLines: string[] = [];
    let skipBlankLineAfterSectionLabel = false;

    for (const line of lines) {
      if (skipBlankLineAfterSectionLabel && !line.trim()) {
        skipBlankLineAfterSectionLabel = false;
        continue;
      }

      skipBlankLineAfterSectionLabel = false;

      const section = this.extractSection(line);
      if (!section) {
        normalizedLines.push(line);
        continue;
      }

      normalizedLines.push(`${section.label}:`);
      if (section.value) {
        normalizedLines.push(section.value);
      } else {
        skipBlankLineAfterSectionLabel = true;
      }
    }

    return normalizedLines;
  }

  private extractSection(line: string): { label: string; value: string } | null {
    const match = line.trim().match(/^([^:]+):\s*(.*)$/);
    if (!match) {
      return null;
    }

    const label = match[1].trim();
    return SECTION_LABELS.has(label) ? { label, value: match[2].trim() } : null;
  }

  private extractFieldLabel(line: string): string | null {
    const match = line.trim().match(/^- ([^:]+):/);
    return match ? match[1].trim() : null;
  }
}
